import {PracticeApptReason} from "@services/types";
import {ApptSlot} from "@services/monolith/availability";
import {useTranslation} from "ni18n";
import React, {memo, useCallback, useMemo} from "react";
import {v4} from "uuid";

import {QueryState} from "../../../hooks/useQueryController";
import {getVirtualSpecialtyIds} from "../../../hooks/useSoonestVirtualSlot";
import {getSchedulingRedirectUrl} from "../../../utils/getRedirectUrl";
import {getStateCodeFromRegionSlug} from "../../../utils/stateUtils";
import Glimmer, {GlimmerOffset} from "../Glimmer";
import ClinicListRow from "./ClinicListRow";
import {sendClinicListRowClickEvent} from "./events";
import {useUserSelectedLocation} from "../../../utils/browser-storage/userSelectedLocation";

type Props = {
  apptReason: PracticeApptReason;
  hideImages: boolean;
  soonestVirtualSlotQuery: QueryState<ApptSlot>;
  rank: number;
  viewId?: string;
};

const Loading = React.memo(function Loading() {
  return (
    <div className="df pv2">
      <Glimmer
        height={50}
        width={50}
        style={{borderRadius: "50%", flexShrink: 0}}
        offset={GlimmerOffset.XXS}
      />
      <div className="dg gg1 gafr w100p ml2">
        <Glimmer height={30} />
        <Glimmer height={22} width="30%" />
      </div>
    </div>
  );
});

const VirtualRow = memo(
  ({apptReason, hideImages, soonestVirtualSlotQuery, rank, viewId}: Props): React.ReactElement => {
    const i18n = useTranslation();
    const {regionSlug: selectedRegion} = useUserSelectedLocation();
    const flowId = useMemo(v4, []);

    const specialtyId = useMemo(() => getVirtualSpecialtyIds(apptReason), [apptReason.id]);
    const preselectedState = useMemo(
      () => getStateCodeFromRegionSlug(selectedRegion),
      [selectedRegion],
    );

    const rowTitle = i18n.t("Book a Virtual Visit");
    const slot = soonestVirtualSlotQuery?.data;
    const onClick = useCallback(() => {
      const soonestSlotTime = soonestVirtualSlotQuery.data?.time;
      if (soonestSlotTime) {
        sendClinicListRowClickEvent(
          rowTitle,
          viewId,
          undefined,
          rank,
          soonestSlotTime,
          undefined,
          flowId,
        );
      }
    }, [flowId, rank, rowTitle, soonestVirtualSlotQuery.data?.time, viewId]);

    const virtualImages = hideImages
      ? []
      : [
          {
            src: "/person-on-phone",
            alt: i18n.t("A happy woman sitting on a sofa with her cell phone"),
          },
          {
            src: "/dad-with-daughter",
            alt: i18n.t("A happy father with his daughter during a virtual appointment"),
          },
        ];

    if (!soonestVirtualSlotQuery.isSettled)
      return (
        <div className="p6 p4-sm">
          <Loading />
        </div>
      );
    // @ts-expect-error TS2322: Type 'null' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
    if (!slot) return null;
    return (
      <ClinicListRow
        images={virtualImages}
        href={getSchedulingRedirectUrl({
          flowId,
          apptReasonId: apptReason.id,
          virtual: true,
          specialtyId,
          // @ts-expect-error TS2322: Type 'string | null' is not assignable to type 'string | undefined'.
          preselectedState,
        })}
        onClick={onClick}
        location={{
          name: rowTitle,
          // @ts-expect-error TS2740: Type '{}' is missing the following properties from type 'Address': firstLine, city, state, zip, and 4 more.
          address: {},
        }}
        soonestSlotTime={slot.time}
        left={
          <div className="bg-darkMint br50 h12 w12 df aic jcc mr3" style={{flexShrink: 0}}>
            <span className="cIcon-phone-video fs26 gray800 darkerGreen" aria-hidden />
          </div>
        }
      />
    );
  },
);

VirtualRow.displayName = "VirtualRow";
export default VirtualRow;
